import React, { useState, useEffect, useRef } from 'react'
import { Menu, Bell, Shield, Check, Sun, Moon } from 'lucide-react'
import { useLocation, Link } from 'react-router-dom'
import { notificationsAPI } from '../../api/axios'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import useEscapeKey from '../../hooks/useEscapeKey'
import { useI18n } from '../../contexts/I18nContext'

const pageTitles = {
  '/dashboard':             'Dashboard',
  '/groups':                'Groups',
  '/expenses':              'Search expenses',
  '/friends':               'Friends',
  '/settle':                'Settle Up',
  '/recurring':             'Recurring',
  '/templates':             'Templates',
  '/insights':              'Insights',
  '/expenses/add':          'Add Expense',
  '/profile':               'Profile',
  '/platform':              'Platform Overview',
  '/platform/users':        'User Management',
  '/platform/groups':       'Group Management',
  '/platform/expenses':     'Expense Monitoring',
  '/platform/settlements':  'Settlement Monitoring',
  '/platform/analytics':    'Platform Analytics',
}

const NOTIFICATION_ICONS = {
  expense_added:       '💸',
  settlement_received: '✅',
  member_joined:       '👋',
  friend_added:        '🤝',
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function notificationLink(n) {
  if (n.data?.expense_id) return `/expenses/${n.data.expense_id}`
  if (n.data?.group_id)   return `/groups/${n.data.group_id}`
  if (n.data?.settlement_id) return '/settle'
  return '/dashboard'
}

export default function TopBar({ onMenuClick }) {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const title = pageTitles[pathname] || 'Tabowe'
  const isPlatformPage = pathname.startsWith('/platform')

  const [open, setOpen]             = useState(false)
  const [notifications, setNotifs]  = useState([])
  const [unreadCount, setUnread]    = useState(0)
  const [loading, setLoading]       = useState(false)
  const dropdownRef = useRef(null)

  // Fetch unread count periodically
  useEffect(() => {
    if (!isAuthenticated) return
    const fetchCount = () => {
      notificationsAPI.unreadCount()
        .then(({ data }) => setUnread(data.data.count))
        .catch(() => {})
    }
    fetchCount()
    const interval = setInterval(fetchCount, 30000) // every 30s
    return () => clearInterval(interval)
  }, [isAuthenticated])

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (!open) return
    setLoading(true)
    notificationsAPI.list()
      .then(({ data }) => setNotifs(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [open])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape — standard a11y for menu-style dropdowns.
  useEscapeKey(() => setOpen(false), open)

  const handleMarkAllRead = async () => {
    await notificationsAPI.markAllAsRead()
    setNotifs(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })))
    setUnread(0)
  }

  const handleClickNotification = async (n) => {
    if (!n.read_at) {
      notificationsAPI.markAsRead(n.id).catch(() => {})
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x))
      setUnread(prev => Math.max(0, prev - 1))
    }
    setOpen(false)
  }

  return (
    <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 md:px-6 lg:px-8 py-3.5 flex items-center gap-4">
      <button
        onClick={onMenuClick}
        className="lg:hidden btn-icon text-gray-500"
        aria-label="Open navigation menu"
      >
        <Menu size={20} />
      </button>

      <h1 className="font-display font-700 text-lg text-forest-900 flex-1">{title}</h1>

      <div className="flex items-center gap-2">
        {isPlatformPage && (
          <div className="flex items-center gap-1.5 bg-gold-50 text-gold-700 px-3 py-1.5 rounded-full text-xs font-semibold">
            <Shield size={13} />
            Platform Admin
          </div>
        )}

        {/* Dark mode toggle */}
        <button
          onClick={toggleTheme}
          className="btn-icon"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notification bell */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen(prev => !prev)}
            className="btn-icon relative"
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full bg-gold-500 text-white text-2xs font-bold flex items-center justify-center px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {open && (
            <div
              className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
              role="menu"
              aria-label="Notifications"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="font-display font-600 text-sm text-forest-900">{t('notifications.title')}</h3>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} className="text-xs text-forest-600 hover:underline flex items-center gap-1">
                    <Check size={12} /> Mark all read
                  </button>
                )}
              </div>

              <div className="max-h-80 overflow-y-auto">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-12 skeleton rounded-lg" />)}
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="py-10 text-center">
                    <Bell size={24} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">{t('notifications.no_notifications')}</p>
                  </div>
                ) : (
                  notifications.map(n => (
                    <Link
                      key={n.id}
                      to={notificationLink(n)}
                      onClick={() => handleClickNotification(n)}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${!n.read_at ? 'bg-forest-50/40' : ''}`}
                    >
                      <span className="text-lg flex-shrink-0 mt-0.5">
                        {NOTIFICATION_ICONS[n.type] || '🔔'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.read_at ? 'font-semibold text-forest-900' : 'text-gray-700'}`}>
                          {n.title}
                        </p>
                        {n.body && <p className="text-xs text-gray-400 truncate">{n.body}</p>}
                        <p className="text-2xs text-gray-300 mt-0.5">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.read_at && (
                        <span className="w-2 h-2 rounded-full bg-forest-500 flex-shrink-0 mt-2" />
                      )}
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
